import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { canSee, normalizePath, branchMatches } from "./sidebarMenu";

// Grace period so the flyout survives the diagonal mouse move between the
// icon and the portaled flyout (they are separate DOM subtrees).
const CLOSE_DELAY_MS = 150;

const Badge = ({ count }) =>
  count > 0 ? (
    <span className="nav-badge">
      <span aria-hidden="true">{count > 99 ? "99+" : count}</span>
      <span className="visually-hidden">{count} unread notifications</span>
    </span>
  ) : null;

// ---------------------------------------------------------------------------
// Expanded sidebar: inline accordion (recursive)
// ---------------------------------------------------------------------------
const SubTree = ({ entries, userRoles, pathname, depth, onNavigate }) => (
  <ul className={`nav-sub nav-sub--d${depth}`}>
    {entries
      .filter((entry) => canSee(entry, userRoles))
      .map((entry) => (
        <SubEntry
          key={entry.name}
          entry={entry}
          userRoles={userRoles}
          pathname={pathname}
          depth={depth}
          onNavigate={onNavigate}
        />
      ))}
  </ul>
);

const SubEntry = ({ entry, userRoles, pathname, depth, onNavigate }) => {
  const children = (entry.submenu || []).filter((c) => canSee(c, userRoles));
  const hasChildren = children.length > 0;
  const branchActive = branchMatches(entry, pathname);
  const leafActive =
    !hasChildren && normalizePath(pathname) === normalizePath(entry.to);

  const [open, setOpen] = useState(branchActive);
  useEffect(() => {
    if (branchActive) setOpen(true);
  }, [branchActive]);

  return (
    <li className="nav-sub-item">
      <div className="nav-row">
        <Link
          to={entry.to}
          className={[
            "nav-link",
            "nav-link--sub",
            leafActive ? "is-active" : "",
            hasChildren && branchActive ? "is-parent-active" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={onNavigate}
          aria-current={leafActive ? "page" : undefined}
        >
          <span className="nav-label">{entry.name}</span>
        </Link>
        {hasChildren && (
          <button
            type="button"
            className="nav-chevron"
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${entry.name}`}
            onClick={() => setOpen((prev) => !prev)}
          >
            <ChevronDown size={14} aria-hidden="true" />
          </button>
        )}
      </div>
      {hasChildren && open && (
        <SubTree
          entries={children}
          userRoles={userRoles}
          pathname={pathname}
          depth={depth + 1}
          onNavigate={onNavigate}
        />
      )}
    </li>
  );
};

// ---------------------------------------------------------------------------
// Icon-only sidebar: flat flyout content
// ---------------------------------------------------------------------------
const FlyoutList = ({ entries, userRoles, pathname, onSelect, depth = 0 }) => (
  <ul className={`nav-flyout-list nav-flyout-list--d${depth}`}>
    {entries
      .filter((entry) => canSee(entry, userRoles))
      .map((entry) => {
        const kids = (entry.submenu || []).filter((c) => canSee(c, userRoles));

        if (kids.length > 0) {
          return (
            <li key={entry.name} className="nav-flyout-group">
              <Link
                to={entry.to}
                className={`nav-flyout-link nav-flyout-link--group ${
                  branchMatches(entry, pathname) ? "is-parent-active" : ""
                }`}
                onClick={onSelect}
              >
                {entry.name}
              </Link>
              <FlyoutList
                entries={kids}
                userRoles={userRoles}
                pathname={pathname}
                onSelect={onSelect}
                depth={depth + 1}
              />
            </li>
          );
        }

        const active = normalizePath(pathname) === normalizePath(entry.to);
        return (
          <li key={entry.name}>
            <Link
              to={entry.to}
              className={`nav-flyout-link ${active ? "is-active" : ""}`}
              onClick={onSelect}
              aria-current={active ? "page" : undefined}
            >
              {entry.name}
            </Link>
          </li>
        );
      })}
  </ul>
);

// ---------------------------------------------------------------------------
// Top-level item
// ---------------------------------------------------------------------------
const SidebarItem = ({
  item,
  isActive,
  userRoles,
  notificationCount = 0,
  collapsed = false,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const itemRef = useRef(null);
  const closeTimerRef = useRef(null);

  const ItemIcon = item.icon;
  const children = (item.submenu || []).filter((c) => canSee(c, userRoles));
  const hasSubmenu = children.length > 0;
  const leafActive = isActive && !hasSubmenu;
  const parentActive = isActive && hasSubmenu;

  // Inline accordion state (expanded sidebar only). Opens automatically when
  // the user lands on one of its routes; the chevron toggles it manually.
  const [open, setOpen] = useState(isActive);
  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  // Icon-only mode: flyout (items with a submenu) and tooltip (leaf items).
  const [flyoutPos, setFlyoutPos] = useState(null);
  const [tipPos, setTipPos] = useState(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const sidebarEdge = () => {
    const sidebar = itemRef.current && itemRef.current.closest(".sidebar");
    return sidebar ? sidebar.getBoundingClientRect().right : null;
  };

  const openFlyout = useCallback(() => {
    clearCloseTimer();
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const edge = sidebarEdge();
    const top = Math.max(8, rect.top);
    setFlyoutPos({
      top,
      left: (edge ?? rect.right) + 4,
      maxHeight: Math.max(160, window.innerHeight - top - 16),
    });
  }, [clearCloseTimer]);

  const scheduleCloseFlyout = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(
      () => setFlyoutPos(null),
      CLOSE_DELAY_MS,
    );
  }, [clearCloseTimer]);

  const showTip = () => {
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const edge = sidebarEdge();
    setTipPos({
      top: rect.top + rect.height / 2,
      left: (edge ?? rect.right) + 8,
    });
  };

  const handleEnter = () => {
    if (!collapsed) return;
    if (hasSubmenu) openFlyout();
    else showTip();
  };
  const handleLeave = () => {
    if (!collapsed) return;
    if (hasSubmenu) scheduleCloseFlyout();
    else setTipPos(null);
  };
  const handleBlur = (e) => {
    if (!collapsed) return;
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (hasSubmenu) scheduleCloseFlyout();
      else setTipPos(null);
    }
  };

  // The flyout / tooltip use `position: fixed` coordinates measured on open,
  // so close them if the page or the menu scrolls or the window resizes.
  useEffect(() => {
    if (!flyoutPos && !tipPos) return undefined;
    const close = (e) => {
      if (
        e &&
        e.target &&
        e.target.nodeType === 1 &&
        e.target.closest(".nav-flyout")
      ) {
        return; // scrolling inside the flyout itself
      }
      setFlyoutPos(null);
      setTipPos(null);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [flyoutPos, tipPos]);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  const closeFloating = () => {
    clearCloseTimer();
    setFlyoutPos(null);
    setTipPos(null);
  };

  // Navigation is handled by <Link>; Teams keeps its existing role-based
  // landing route.
  const handleClick = (e) => {
    if (item.name === "Teams") {
      e.preventDefault();
      if (
        userRoles.includes("ROLE_MANAGER") ||
        userRoles.includes("ROLE_ADMIN")
      ) {
        navigate("/team/mark-attendance");
      } else if (userRoles.includes("ROLE_HR")) {
        navigate("/team/comp-payroll");
      } else {
        navigate("/team");
      }
    }
    closeFloating();
    if (onNavigate) onNavigate();
  };

  const linkLabel =
    collapsed && notificationCount > 0
      ? `${item.name}, ${notificationCount} unread notifications`
      : collapsed
        ? item.name
        : undefined;

  return (
    <li
      ref={itemRef}
      className={`nav-item ${collapsed ? "nav-item--collapsed" : ""}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleBlur}
    >
      <div className="nav-row">
        <Link
          to={item.to}
          className={[
            "nav-link",
            "nav-link--top",
            leafActive ? "is-active" : "",
            parentActive ? "is-parent-active" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={handleClick}
          aria-current={leafActive ? "page" : undefined}
          aria-label={linkLabel}
        >
          <span className="nav-icon-wrap">
            <ItemIcon size={19} strokeWidth={1.8} aria-hidden="true" />
            {collapsed && notificationCount > 0 && (
              <span className="nav-badge nav-badge--dot" aria-hidden="true" />
            )}
          </span>
          {!collapsed && <span className="nav-label">{item.name}</span>}
          {!collapsed && <Badge count={notificationCount} />}
        </Link>

        {!collapsed && hasSubmenu && (
          <button
            type="button"
            className="nav-chevron"
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${item.name} menu`}
            onClick={() => setOpen((prev) => !prev)}
          >
            <ChevronDown size={15} aria-hidden="true" />
          </button>
        )}
      </div>

      {!collapsed && hasSubmenu && open && (
        <SubTree
          entries={children}
          userRoles={userRoles}
          pathname={pathname}
          depth={1}
          onNavigate={onNavigate}
        />
      )}

      {collapsed &&
        hasSubmenu &&
        flyoutPos &&
        createPortal(
          <div
            className="nav-flyout"
            role="group"
            aria-label={`${item.name} menu`}
            style={{
              top: flyoutPos.top,
              left: flyoutPos.left,
              maxHeight: flyoutPos.maxHeight,
            }}
            onMouseEnter={openFlyout}
            onMouseLeave={scheduleCloseFlyout}
          >
            <Link
              to={item.to}
              className="nav-flyout-title"
              onClick={handleClick}
            >
              {item.name}
            </Link>
            <FlyoutList
              entries={children}
              userRoles={userRoles}
              pathname={pathname}
              onSelect={() => {
                closeFloating();
                if (onNavigate) onNavigate();
              }}
            />
          </div>,
          document.body,
        )}

      {collapsed &&
        !hasSubmenu &&
        tipPos &&
        createPortal(
          <div
            className="nav-tooltip"
            role="tooltip"
            style={{ top: tipPos.top, left: tipPos.left }}
          >
            {item.name}
          </div>,
          document.body,
        )}
    </li>
  );
};

export default SidebarItem;
